import { FIELD_BINDING_CATALOGUE } from './fieldBindingCatalogue';
import type { CollectionItemField, FieldBindingCollection } from './fieldBindingCatalogue';

/**
 * The programme switches (`KycSetting` columns) that decide whether investors are asked for a
 * piece of data at all.
 *
 * A data point that reads data behind a switch the programme has off is not offered for binding:
 * nobody on the programme was asked for it, so it renders blank — or, for a declaration, the
 * `false` an unanswered one is stored as.
 */
export const PROGRAMME_DATA_REQUIREMENTS = [
	'isKycRequired',
	'isInvestorBankAccountRequired',
	'isTaxResidenceRequired',
	'isTinRequired',
	'isPlaceOfBirthRequired',
	'isCountryOfBirthRequired',
	'isEmployeeIdRequired',
	'isCitizenshipRequired',
	'isBeneficialOwnershipRequired',
	'isSourceOfFundsRequired',
	'isPepRequired',
] as const;

export type ProgrammeDataRequirement = (typeof PROGRAMME_DATA_REQUIREMENTS)[number];

/**
 * True when the programme collects everything `requires` names.
 *
 * `uncollected` is strings, as the catalogue response carries them; a value this does not know
 * withholds nothing.
 */
export const isCollected = (
	requires: readonly ProgrammeDataRequirement[] | undefined,
	uncollected: readonly string[]
): boolean => requires?.every((requirement) => !uncollected.includes(requirement)) ?? true;

/**
 * Whether the programme collects what the catalogue point at `path` reads. A path outside the
 * static catalogue — a per-scheme point — reads nothing a switch controls.
 */
export const isDataPointCollected = (path: string, uncollected: readonly string[]): boolean =>
	isCollected(
		FIELD_BINDING_CATALOGUE.find((point) => point.path === path)?.requires,
		uncollected
	);

export const isCollectionCollected = (
	collection: FieldBindingCollection,
	uncollected: readonly string[]
): boolean => isCollected(collection.requires, uncollected);

export const isItemFieldCollected = (
	collection: FieldBindingCollection,
	itemField: CollectionItemField,
	uncollected: readonly string[]
): boolean =>
	isCollectionCollected(collection, uncollected) && isCollected(itemField.requires, uncollected);
